import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight, ShoppingCart } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Chanderi Silk Saree',
            size: 'M',
            price: 8999,
            quantity: 1,
            image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 2,
            name: 'Banarasi Kurta Set',
            size: 'L',
            price: 6499,
            quantity: 2,
            image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(prev => prev.filter(item => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = subtotal > 999 ? 0 : 149;
    const total = subtotal + shipping;

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header cartCount={cartItems.length} />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            {/* Page Header */}
            <div className="pt-20 md:pt-24 pb-8 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Your Selection</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">Shopping Cart</h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                {cartItems.length === 0 ? (
                    <div className="text-center py-20">
                        <ShoppingCart size={48} className="mx-auto mb-4 text-[#E2E8F0]" />
                        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Your cart is empty</h2>
                        <p className="text-sm text-[#64748B] mb-6 font-light">Discover our curated collection of Indian fashion</p>
                        <Button variant="primary" size="lg" onClick={() => navigate('/template9/shop')}>
                            Continue Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Cart Items */}
                        <div className="lg:col-span-2">
                            <div className="space-y-4">
                                {cartItems.map((item) => (
                                    <div key={item.id} className="flex gap-4 p-4 border border-[#E2E8F0] rounded-none relative">
                                        <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-20 h-24 sm:w-24 sm:h-32 object-cover rounded-none flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm sm:text-base font-medium text-[#0F172A] truncate">{item.name}</h3>
                                            <p className="text-xs text-[#64748B] mt-1">Size: {item.size}</p>
                                            <p className="text-sm font-semibold text-[#2563EB] mt-2">
                                                {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                            </p>
                                            <div className="flex items-center justify-between mt-3">
                                                <div className="flex items-center border border-[#E2E8F0] rounded-none">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, -1)}
                                                        className="w-8 h-8 flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-[#F8FAFC]"
                                                    >
                                                        <Minus size={14} className="text-[#64748B]" />
                                                    </button>
                                                    <span className="w-8 h-8 flex items-center justify-center text-sm font-medium text-[#0F172A] border-l border-r border-[#E2E8F0]">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, 1)}
                                                        className="w-8 h-8 flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-[#F8FAFC]"
                                                    >
                                                        <Plus size={14} className="text-[#64748B]" />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    className="text-[#64748B] hover:text-red-500 bg-transparent border-none cursor-pointer transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div>
                            <div className="border border-[#E2E8F0] rounded-none p-6 sticky top-24">
                                <h3 className="text-lg font-semibold text-[#0F172A] mb-6 uppercase tracking-wider">Order Summary</h3>
                                <div className="w-8 h-0.5 bg-[#2563EB] mb-6" />

                                {/* Coupon */}
                                <div className="flex gap-2 mb-6">
                                    <div className="flex-1 relative">
                                        <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                                        <input
                                            type="text"
                                            placeholder="Coupon code"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E2E8F0] rounded-none focus:outline-none focus:border-[#2563EB] bg-white"
                                        />
                                    </div>
                                    <Button variant="secondary" size="sm">Apply</Button>
                                </div>

                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#64748B] font-light">Subtotal</span>
                                        <span className="text-[#0F172A] font-medium">{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#64748B] font-light">Shipping</span>
                                        <span className="text-[#0F172A] font-medium">
                                            {shipping === 0 ? 'Free' : `\u20B9${shipping}`}
                                        </span>
                                    </div>
                                    {shipping === 0 && (
                                        <p className="text-xs text-green-600 font-medium">You qualify for free shipping!</p>
                                    )}
                                </div>

                                <div className="flex justify-between text-base font-bold border-t border-[#E2E8F0] pt-4 mb-6">
                                    <span className="text-[#0F172A]">Total</span>
                                    <span className="text-[#2563EB]">{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                                </div>

                                <Button
                                    variant="primary"
                                    size="lg"
                                    fullWidth
                                    onClick={() => navigate('/template9/checkout')}
                                    icon={ArrowRight}
                                >
                                    Proceed to Checkout
                                </Button>

                                <button
                                    onClick={() => navigate('/template9/shop')}
                                    className="w-full mt-3 text-center text-sm text-[#2563EB] hover:text-[#1D4ED8] bg-transparent border-none cursor-pointer font-medium"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
